import { Capacitor } from '@capacitor/core';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { COURSES_NOTES_WEB_FLAG } from '@/constants/courses';

/**
 * Whether THIS BUILD may offer the Next Steps notes features at all: highlights,
 * the note drawer, standalone notes, tags, the Notes Hub and the blank inputs.
 *
 * Decision 7 of the encryption spec (docs/plans/2026-09-02-next-steps-e2e-encryption.md,
 * section 6.10): encrypted notes are a native feature. The member key lives in the
 * iOS Keychain and in Android backup, and a web build has nothing safe to hold a
 * key in — so production web offers none of these. Lessons stay readable; only the
 * annotation surfaces go, and `/courses/notes` sends a web visitor back to the
 * courses home.
 *
 * ONE exception, for the test suite. The courses Playwright specs exercise
 * highlights, notes and tags in a browser, and that coverage must not be lost.
 * `courses-notes-web` re-enables the features on web (later tickets give it an
 * IndexedDB-only key store). The row is seeded OFF everywhere by hogc-one-api
 * PR #1083 and switched on by hand in development and staging only, never in
 * production. The dev-only Next Steps mock console also seeds it on locally.
 *
 * WHY THIS IS A UTIL AND NOT A STORE GETTER. The courses store answers "what does
 * the SERVER grant this member" (`courseFeatures`, `canUseNoteFeatures`: tiers,
 * leader access, the per-program `noteHub` flag). This answers a different
 * question — "what can this BUILD hold safely" — and folding it into the store
 * would make every access spec depend on Capacitor and the flags composable, and
 * would route the web case through the Hub's own Home redirect where the spec
 * wants the courses home. So it is applied at the call sites, ANDed with the
 * server answer, and a native build reads exactly what it read before: `true`.
 *
 * Platform is read from `Capacitor.isNativePlatform()`, as the spec names it. A
 * phone BROWSER is web for this purpose (Ionic would call it `mobileweb`), which
 * is right: a Safari tab has no Keychain either.
 */

/**
 * The rule itself, with no I/O: native always; web only under the flag.
 *
 * @param {{ isNative?: boolean, flagOn?: boolean }} answers
 * @returns {boolean}
 */
export function notesAvailableOn({ isNative, flagOn } = {}) {
  if (isNative === true) return true;
  return flagOn === true;
}

/** The platform seam, so the router and the unit specs read one probe. */
export function isNativeNotesPlatform() {
  return Capacitor.isNativePlatform();
}

/**
 * The wired answer for component computeds. Resolved at CALL time: flags load
 * over the network after boot and `isEnabled` reads a reactive Map, so a computed
 * that calls this re-evaluates when they land, and a spec can mock the composable
 * before anything reads it (the same reason the reader resolves its other flags
 * inside computeds rather than at module scope).
 *
 * Native short-circuits before the flag is read at all: the flag is a web-only
 * switch and must have no say on a native build, in either direction.
 *
 * @returns {boolean}
 */
export function coursesNotesAvailable() {
  if (isNativeNotesPlatform()) return true;
  const { isEnabled } = useFeatureFlags();
  return notesAvailableOn({ isNative: false, flagOn: isEnabled(COURSES_NOTES_WEB_FLAG) });
}
